import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "@/components/customer/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, Search, AlertTriangle, RefreshCw } from "lucide-react";
import { api, CHF, PHONE_DISPLAY, PHONE_TEL } from "@/lib/apiClient";
import { STATUS_LABELS, STATUS_COLOR, flowFor, normalizeStatus } from "@/lib/statuses";
import { toast } from "sonner";

const getMineIds = () => {
  try { return JSON.parse(localStorage.getItem("ata_my_orders") || "[]"); } catch { return []; }
};

const Stepper = ({ order }) => {
  if (["refused", "cancelled"].includes(order.status)) return null;
  const steps = flowFor(order.mode);
  const cur = steps.indexOf(normalizeStatus(order.status));
  return (
    <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`stepper-${order.order_number}`}>
      {steps.map((s, i) => (
        <span key={s} className={`text-[11px] px-2 py-1 border transition-colors ${i <= cur ? "bg-brand-red text-white border-brand-red" : "border-neutral-300 text-neutral-400"}`}>
          {i < cur ? "✓ " : ""}{STATUS_LABELS[s]}
        </span>
      ))}
    </div>
  );
};

export default function MesCommandes() {
  const [params] = useSearchParams();
  const highlight = params.get("order");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(localStorage.getItem("ata_last_email") || "");
  const [mode, setMode] = useState(getMineIds().length > 0 ? "mine" : "email");

  const loadMine = useCallback(async () => {
    const ids = getMineIds();
    if (ids.length === 0) { setOrders([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/orders/mine", { params: { ids: ids.join(",") } });
      setOrders(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  const searchEmail = useCallback(async () => {
    if (!email.trim()) return toast.error("Entrez votre email");
    setMode("email");
    setLoading(true);
    try {
      const { data } = await api.get("/orders/track", { params: { email: email.trim() } });
      setOrders(data);
      localStorage.setItem("ata_last_email", email.trim());
    } catch { toast.error("Erreur"); } finally { setLoading(false); }
  }, [email]);

  useEffect(() => {
    if (getMineIds().length > 0) loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh status in real time
  useEffect(() => {
    const t = setInterval(() => {
      if (mode === "email" && email.trim()) {
        api.get("/orders/track", { params: { email: email.trim() } }).then((r) => setOrders(r.data)).catch(() => {});
      } else if (getMineIds().length > 0) {
        loadMine();
      }
    }, 15000);
    return () => clearInterval(t);
  }, [mode, email, loadMine]);

  const hasMine = getMineIds().length > 0;

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-4xl font-extrabold tracking-tighter">Ma commande</h1>
          <button onClick={() => (mode === "email" ? searchEmail() : loadMine())} className="flex items-center gap-1 text-sm text-brand-red font-medium" data-testid="track-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
        </div>
        <p className="text-muted-foreground mt-2">Suivez l'état de votre commande en temps réel.</p>

        <div className="mt-6 bg-brand-amber text-black p-4 border-2 border-black flex items-start gap-3" data-testid="track-alert-banner">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <p className="text-sm font-medium">Si votre commande n'est pas <strong>confirmée</strong> après 2 minutes, appelez-nous directement au <a href={`tel:${PHONE_TEL}`} className="underline font-bold">{PHONE_DISPLAY}</a>.</p>
        </div>

        {orders && orders.length === 0 && (
          <p className="mt-8 text-center text-muted-foreground" data-testid="no-orders">
            {hasMine ? "Aucune commande trouvée." : "Aucune commande récente sur cet appareil."}
          </p>
        )}

        <div className="mt-6 space-y-4">
          {orders?.map((o) => {
            const isNew = highlight && String(o.order_number) === String(highlight);
            return (
              <div key={o.id} className={`border-2 p-4 ${isNew ? "border-brand-red ring-2 ring-brand-red/30" : "border-black"}`} data-testid={`track-order-${o.order_number}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display text-2xl font-extrabold">#{o.order_number}{isNew && <span className="ml-2 text-xs font-sans font-medium text-brand-red">Nouvelle</span>}</span>
                  <span className={`text-xs font-bold px-3 py-1 ${STATUS_COLOR[o.status]}`} data-testid={`track-status-${o.order_number}`}>{STATUS_LABELS[o.status]}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{o.mode === "delivery" ? "Livraison" : "À emporter"} · {o.timeslot}</p>
                <Stepper order={o} />
                <div className="mt-3 border-t border-black/10 pt-3 space-y-1">
                  {o.items.map((i, idx) => <div key={idx} className="flex justify-between text-sm"><span>{i.qty}× {i.name}</span></div>)}
                </div>
                <div className="flex justify-between font-bold mt-3 pt-3 border-t border-black/10"><span>Total</span><span>{CHF(o.total)}</span></div>
              </div>
            );
          })}
        </div>

        {/* Secondary: retrouver une commande par email (autre appareil) */}
        <details className="mt-8 border border-black/15 p-4" open={!hasMine} data-testid="email-lookup">
          <summary className="cursor-pointer text-sm font-medium">Retrouver une commande par email (autre appareil)</summary>
          <div className="mt-3 flex gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchEmail()} placeholder="votre@email.com" className="rounded-none border-black" data-testid="track-email-input" />
            <Button onClick={searchEmail} disabled={loading} className="rounded-none bg-black" data-testid="track-search-btn"><Search className="w-4 h-4" /></Button>
          </div>
        </details>

        <div className="mt-6 bg-brand-red text-white p-4 flex items-center justify-between" data-testid="track-call-cta">
          <span className="text-sm font-medium">Une question sur votre commande ?</span>
          <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2 font-bold"><Phone className="w-4 h-4" /> {PHONE_DISPLAY}</a>
        </div>
      </div>
    </div>
  );
}
